/** In-memory challenge response. The key never enters an envelope, URL, store or log. */
export type ControlAccessStatus = "local" | "locked" | "pending" | "authorized" | "rejected";

export interface ControlAccessClientOptions {
  required: boolean;
  room: string;
  sid: string;
  peer: string;
  send: (text: string) => boolean;
  onStatus: (status: ControlAccessStatus) => void;
}

export function createControlAccessClient(opts: ControlAccessClientOptions) {
  let status: ControlAccessStatus = opts.required ? "locked" : "local";
  let key: string | null = null;
  let nonce: string | null = null;
  let grant: string | null = null;
  let backendEpoch: unknown = null;
  let generation = 0;
  let timeout: ReturnType<typeof setTimeout> | null = null;
  const publish = (next: ControlAccessStatus) => { status = next; opts.onStatus(next); };
  const clear = () => {
    key = null; nonce = null; generation++;
    if (timeout) clearTimeout(timeout);
    timeout = null;
  };
  function reset() {
    clear(); grant = null;
    publish(opts.required ? "locked" : "local");
  }
  function authorize(secret: string): boolean {
    if (!opts.required) return true;
    clear(); grant = null;
    if (!/^[0-9a-f]{64}$/.test(secret.trim())) { publish("rejected"); return false; }
    key = secret.trim();
    publish("pending");
    timeout = setTimeout(() => { clear(); publish("rejected"); }, 30_000);
    if (!opts.send(JSON.stringify({ event: "control-auth-request", payload: { sid: opts.sid } }))) {
      clear(); publish("rejected"); return false;
    }
    return true;
  }
  async function receive(message: unknown): Promise<void> {
    let env: { event?: string; payload?: Record<string, unknown> };
    try { env = typeof message === "string" ? JSON.parse(message) : message; } catch { return; }
    if (!env || typeof env !== "object") return;
    const payload = env.payload;
    if (opts.required && env.event === "robot-config" && payload?.control_access_epoch &&
        payload.control_access_epoch !== backendEpoch) {
      const previousEpoch = backendEpoch;
      backendEpoch = payload.control_access_epoch;
      if (previousEpoch !== null) reset();
    }
    if (env.event === "control-auth-reset" && payload &&
        (payload.peer == null || payload.peer === opts.peer)) {
      // The request itself stops/revokes an older grant. Keep THIS pending key
      // only for that explicit request; connection/monitor resets still forget it.
      if (!(status === "pending" && payload.reason === "reauthorization" && payload.reauthorizing_peer === opts.peer)) reset();
      return;
    }
    if (!opts.required || !payload || payload.peer !== opts.peer || payload.sid !== opts.sid) return;
    if (env.event === "control-auth-challenge" && status === "pending" && key && nonce === null &&
        payload.room === opts.room && typeof payload.nonce === "string" && /^[0-9a-f]{64}$/.test(payload.nonce)) {
      const myGeneration = generation;
      nonce = payload.nonce;
      const secret = key;
      key = null;
      try {
        const encoder = new TextEncoder();
        const cryptoKey = await crypto.subtle.importKey("raw", encoder.encode(secret),
          { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
        const content = JSON.stringify(["panther-control-v1", opts.room, opts.sid, opts.peer, nonce]);
        const signature = await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(content));
        if (generation !== myGeneration) return;
        const proof = Array.from(new Uint8Array(signature), (b) => b.toString(16).padStart(2, "0")).join("");
        if (!opts.send(JSON.stringify({ event: "control-auth-proof", payload: { sid: opts.sid, nonce, proof } }))) {
          clear(); publish("rejected");
        }
      } catch { clear(); publish("rejected"); }
    } else if (env.event === "control-auth-result" && status === "pending" && nonce && payload.nonce === nonce) {
      const accepted = payload.ok === true;
      grant = accepted ? nonce : null;
      clear(); publish(accepted ? "authorized" : "rejected");
    }
  }
  function allows(text: string) {
    if (!opts.required) return true;
    try {
      const env = JSON.parse(text);
      if (["get-capabilities", "video-clock-ping", "video-frame-probe"].includes(env.event)) return true;
      return status === "authorized" && (env.payload?.sid == null || env.payload.sid === opts.sid);
    } catch { return false; }
  }
  function protect(text: string): string | null {
    if (!allows(text)) return null;
    if (!opts.required) return text;
    const env = JSON.parse(text);
    return JSON.stringify({ ...env, payload: { ...env.payload, auth_nonce: grant } });
  }
  return { authorize, receive, allows, protect, reset, getStatus: () => status };
}
